import React, { useState, useEffect } from 'react';
import useGroupLabels from 'hooks/useGroupLabels';
import useOrganisationWords from 'hooks/useOrganisationWords';
import { formatGroupName } from 'utils/groupLabel';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styles from './ClubRecordEditPopup.module.css';
import BackIcon from 'assets/images/icon_arrow_right.svg';
import CloseIcon from 'assets/images/icon_close.svg';
import CustomButton, {
  defaultButtonStyle,
} from 'components/common/CustomButton';
import { redColor } from 'config/global';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import {
  convertMillisecondsToRecord,
  convertRecordToMilliseconds,
  dateFieldToServerDateTime,
  toDateFieldValue,
  todayDateFieldValue,
} from 'utils/time';
import FormSelect from 'components/common/FormSelect';

const GENDER_OPTIONS = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

const TRACK_SURFACE_OPTIONS = [
  { value: 'Synthetic', label: 'Synthetic' },
  { value: 'Grass', label: 'Grass' },
];

// Helper function to determine if event is a time-based event
const isTimeEvent = (eventType) => {
  const fieldEvents = [
    'High Jump',
    'Long Jump',
    'Triple Jump',
    'Shot Put',
    'Discus',
    'Javelin',
    'Vortex',
  ];
  return eventType && !fieldEvents.includes(eventType);
};

// Helper function to determine if field event requires track surface (has runup)
const fieldEventHasRunup = (eventType) => {
  const fieldEventsWithRunup = [
    'High Jump',
    'Long Jump',
    'Triple Jump',
    'Javelin',
  ];
  return eventType && fieldEventsWithRunup.includes(eventType);
};

// Helper function to format record value for display
const formatRecordValueForDisplay = (rawValue, eventType) => {
  if (!rawValue || !eventType) return '';

  if (isTimeEvent(eventType)) {
    // Time event - convert milliseconds to display format
    return convertMillisecondsToRecord(parseFloat(rawValue), 2);
  } else {
    // Field event - already in meters, format to 2 decimal places
    return parseFloat(rawValue).toFixed(2);
  }
};

// Helper function to convert display value to raw value for API
const convertDisplayValueToRaw = (displayValue, eventType) => {
  if (!displayValue || !eventType) return 0;

  if (isTimeEvent(eventType)) {
    // Time event - convert display format to milliseconds
    return convertRecordToMilliseconds(displayValue);
  } else {
    // Field event - already in meters
    return parseFloat(displayValue);
  }
};

// Helper function to split full name into first and last name
const splitAthleteName = (fullName) => {
  if (!fullName) return { firstName: '', lastName: '' };

  const nameParts = fullName.trim().split(' ');
  if (nameParts.length === 1) {
    return { firstName: nameParts[0], lastName: '' };
  }

  // Handle names with multiple parts - take first as firstName, rest as lastName
  const firstName = nameParts[0];
  const lastName = nameParts.slice(1).join(' ');

  return { firstName, lastName };
};

const ClubRecordEditPopup = ({
  showPopup,
  closePopup,
  mode = 'edit', // 'edit', 'add'
  selectedRecord = null,
  eventOptions = [], // when adding: the club's events (utils/eventTypeOptions, clubRecordEventOptions)
  ageOptions = [], // when adding: the club's own ages, one at a time (utils/clubRecordAges)
  onSave,
  onDelete,
}) => {
  // A record is for one age (or year), stored as the group number the rest of the system uses (Y5 is 10). When
  // adding, the choices are the club's own, from its age groups (utils/clubRecordAges): a fixed list offered 5 to
  // 20, Senior and Masters to every club (Doug, 2026-10-23).
  const { groupingBasis, words } = useGroupLabels();
  // "Add Club Record", or "Add School Record" for a school.
  const organisation = useOrganisationWords();
  const [formData, setFormData] = useState({
    eventType: '',
    athleteAge: '',
    gender: '',
    trackSurface: '',
    athleteFirstName: '',
    athleteLastName: '',
    recordValue: '',
    achievedAt: '',
    approvedBy: '',
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [originalAchievedAt, setOriginalAchievedAt] = useState(null);

  // Determine if current event type is a field event
  const isFieldEvent = formData.eventType && !isTimeEvent(formData.eventType);

  // Load data when editing
  useEffect(() => {
    if (mode === 'edit' && selectedRecord) {
      // Split the athleteName into first and last name
      const { firstName, lastName } = splitAthleteName(
        selectedRecord.athleteName
      );

      // Store the original achievedAt for time preservation
      setOriginalAchievedAt(selectedRecord.achievedAt);

      setFormData({
        eventType: selectedRecord.eventType || '',
        athleteAge:
          selectedRecord.athleteAge?.toString() ||
          selectedRecord.ageGroup?.toString() ||
          '',
        gender: selectedRecord.gender || '',
        trackSurface: selectedRecord.trackSurface || '',
        athleteFirstName: firstName,
        athleteLastName: lastName,
        recordValue: formatRecordValueForDisplay(
          selectedRecord.recordValue,
          selectedRecord.eventType
        ),
        achievedAt: toDateFieldValue(selectedRecord.achievedAt),
        approvedBy: selectedRecord.approvedBy || '',
      });
    } else if (mode === 'add') {
      setFormData({
        eventType: '',
        athleteAge: '',
        gender: '',
        trackSurface: 'Synthetic',
        athleteFirstName: '',
        athleteLastName: '',
        recordValue: '',
        achievedAt: todayDateFieldValue(), // today here, not in UTC
        approvedBy: '',
      });
      setOriginalAchievedAt(null);
    }
    setErrors({});
  }, [mode, selectedRecord, showPopup]);

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }

    // If event type changes, update track surface appropriately
    if (name === 'eventType' && value) {
      const isField = !isTimeEvent(value);
      const hasRunup = fieldEventHasRunup(value);

      // Clear track surface only for field events WITHOUT runup (Shot Put, Discus)
      if (isField && !hasRunup) {
        setFormData((prev) => ({
          ...prev,
          trackSurface: null,
        }));
      } else if (!formData.trackSurface) {
        // If switching to track event or field event with runup and no surface set, default to Synthetic
        setFormData((prev) => ({
          ...prev,
          trackSurface: 'Synthetic',
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.eventType.trim())
      newErrors.eventType = 'Event type is required';
    if (!formData.athleteAge.trim())
      newErrors.athleteAge = `${words.sentence.group} is required`;
    if (!formData.gender.trim()) newErrors.gender = 'Gender is required';

    // Validate track surface for track events and field events with runup
    const needsTrackSurface = !isFieldEvent || fieldEventHasRunup(formData.eventType);
    if (needsTrackSurface && !formData.trackSurface) {
      newErrors.trackSurface = 'Track surface is required';
    }

    if (!formData.athleteFirstName.trim())
      newErrors.athleteFirstName = 'First name is required';
    if (!formData.athleteLastName.trim())
      newErrors.athleteLastName = 'Last name is required';
    if (!formData.recordValue.trim())
      newErrors.recordValue = 'Record value is required';
    if (!formData.achievedAt.trim()) newErrors.achievedAt = 'Date is required';
    if (!formData.approvedBy.trim())
      newErrors.approvedBy = 'Approving official is required';

    // Validate record value format (time or distance)
    if (formData.recordValue.trim()) {
      const eventType =
        formData.eventType ||
        (mode === 'edit' ? selectedRecord?.eventType : '');

      if (isTimeEvent(eventType)) {
        // Time format validation (mm:ss.dd or ss.dd)
        try {
          convertRecordToMilliseconds(formData.recordValue.trim());
        } catch (error) {
          newErrors.recordValue =
            'Time must be in format mm:ss.dd or ss.dd (e.g., 2:13.45 or 13.45)';
        }
      } else {
        // Distance format validation (decimal number)
        const distanceRegex = /^\d+(\.\d{1,2})?$/;
        if (!distanceRegex.test(formData.recordValue.trim())) {
          newErrors.recordValue =
            'Distance must be a valid number (e.g., 12.34)';
        }
      }
    }

    return newErrors;
  };

  const handleSave = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);

    try {
      // The date as typed, sent as a local date and time with no time zone (the time of day kept when editing).
      // It was sent through toISOString(), so 20 October typed in Australia was stored as 19 October 13:00.
      const achievedAtISO = dateFieldToServerDateTime(
        formData.achievedAt,
        originalAchievedAt
      );
      const approvedDateISO = new Date().toISOString();

      // Convert record value from display format to raw value
      const eventType =
        formData.eventType ||
        (mode === 'edit' ? selectedRecord?.eventType : '');
      const rawRecordValue = convertDisplayValueToRaw(
        formData.recordValue,
        eventType
      );

      // Prepare record data with athleteAge as integer
      // Track surface is required for track events and field events with runup
      const needsTrackSurface = !isFieldEvent || fieldEventHasRunup(formData.eventType);

      const recordData = {
        eventType: formData.eventType,
        athleteAge: parseInt(formData.athleteAge), // Use athleteAge, not ageGroup
        gender: formData.gender,
        trackSurface: needsTrackSurface ? formData.trackSurface : null,
        athleteFirstName: formData.athleteFirstName,
        athleteLastName: formData.athleteLastName,
        recordValue: rawRecordValue,
        achievedAt: achievedAtISO,
        approvedBy: formData.approvedBy,
        approvedDate: approvedDateISO,
      };

      // If editing, include the recordId
      if (mode === 'edit' && selectedRecord?.recordId) {
        recordData.recordId = selectedRecord.recordId;
      }

      // If editing, include the clubId if available
      if (selectedRecord?.clubId) {
        recordData.clubId = selectedRecord.clubId;
      }

      await onSave(recordData);
      closePopup();
    } catch (error) {
      console.error('Error saving record:', error);
      const errorMessage =
        error?.response?.data || error?.message || 'Failed to save record';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);

    try {
      await onDelete(selectedRecord.recordId);
      setShowDeleteConfirm(false);
      closePopup();
    } catch (error) {
      console.error('Error deleting record:', error);
      const errorMessage =
        error?.response?.data || error?.message || 'Failed to delete record';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClosePopup = () => {
    setIsLoading(false);
    setShowDeleteConfirm(false);
    closePopup();
  };

  return (
    <div
      className={`${styles.clubRecordEditPopupOverlay} ${
        showPopup ? styles.active : ''
      }`}
    >
      <div className={styles.clubRecordEditPopup}>
        <div className={styles.clubRecordEditPopupContainer}>
          <div className={styles.title}>
            {mode === 'add' ? `Add ${organisation.title} Record` : `Edit ${organisation.title} Record`}
          </div>

          <div className={styles.form}>
            {mode === 'edit' ? (
              <>
                {/* Read-only fields for editing */}
                <div className={styles.readOnlyRow}>
                  <div className={styles.readOnlyField}>
                    <label className={styles.label}>Event Type</label>
                    <div className={styles.readOnlyValue}>
                      {formData.eventType}
                    </div>
                  </div>
                  <div className={styles.readOnlyField}>
                    <label className={styles.label}>{words.value}</label>
                    <div className={styles.readOnlyValue}>
                      {formatGroupName(groupingBasis, formData.athleteAge)}
                    </div>
                  </div>
                  <div className={styles.readOnlyField}>
                    <label className={styles.label}>Gender</label>
                    <div className={styles.readOnlyValue}>
                      {formData.gender}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Selectable fields for adding - all in one row */}
                <div className={styles.threeColumnRow}>
                  <FormSelect
                    label="Event Type"
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleInputChange}
                    options={eventOptions}
                    error={errors.eventType}
                    disabled={isLoading}
                    required={false}
                  />

                  <FormSelect
                    label={words.value}
                    name="athleteAge"
                    value={formData.athleteAge}
                    onChange={handleInputChange}
                    options={ageOptions}
                    error={errors.athleteAge}
                    disabled={isLoading}
                    required={false}
                  />

                  <FormSelect
                    label="Gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    options={GENDER_OPTIONS}
                    error={errors.gender}
                    disabled={isLoading}
                    required={false}
                  />
                </div>
              </>
            )}

            {/* Athlete names - always in same row */}
            <div className={styles.athleteNamesRow}>
              <div className={styles.inputGroup}>
                <label className={styles.label}>Athlete First Name</label>
                <input
                  type="text"
                  value={formData.athleteFirstName}
                  onChange={(e) =>
                    handleInputChange('athleteFirstName', e.target.value)
                  }
                  className={`${styles.input} ${errors.athleteFirstName ? styles.inputError : ''}`}
                  disabled={isLoading}
                  placeholder="Enter first name"
                />
                {errors.athleteFirstName && (
                  <span className={styles.errorText}>
                    {errors.athleteFirstName}
                  </span>
                )}
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.label}>Athlete Last Name</label>
                <input
                  type="text"
                  value={formData.athleteLastName}
                  onChange={(e) =>
                    handleInputChange('athleteLastName', e.target.value)
                  }
                  className={`${styles.input} ${errors.athleteLastName ? styles.inputError : ''}`}
                  disabled={isLoading}
                  placeholder="Enter last name"
                />
                {errors.athleteLastName && (
                  <span className={styles.errorText}>
                    {errors.athleteLastName}
                  </span>
                )}
              </div>
            </div>

            {/* Track Surface, Result, and Date - conditional layout */}
            {isFieldEvent && !fieldEventHasRunup(formData.eventType) ? (
              // Field event WITHOUT runup (Shot Put, Discus) - only Result and Date on same row
              <div className={styles.twoColumnRow}>
                <div className={styles.inputGroup}>
                  <label className={styles.label}>Result (meters)</label>
                  <input
                    type="text"
                    value={formData.recordValue}
                    onChange={(e) =>
                      handleInputChange('recordValue', e.target.value)
                    }
                    className={`${styles.input} ${errors.recordValue ? styles.inputError : ''}`}
                    disabled={isLoading}
                    placeholder="e.g., 12.34"
                  />
                  {errors.recordValue && (
                    <span className={styles.errorText}>
                      {errors.recordValue}
                    </span>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <label className={styles.label}>Date Achieved</label>
                  <input
                    type="date"
                    value={formData.achievedAt}
                    onChange={(e) =>
                      handleInputChange('achievedAt', e.target.value)
                    }
                    className={`${styles.input} ${errors.achievedAt ? styles.inputError : ''}`}
                    disabled={isLoading}
                    style={{ color: formData.achievedAt ? '#333' : '#999' }}
                  />
                  {errors.achievedAt && (
                    <span className={styles.errorText}>
                      {errors.achievedAt}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <>
                {/* Track event OR Field event with runup - Result and Date on same row */}
                <div className={styles.twoColumnRow}>
                  <div className={styles.inputGroup}>
                    <label className={styles.label}>
                      {isFieldEvent ? 'Result (meters)' : 'Result (mm:ss.sss)'}
                    </label>
                    <input
                      type="text"
                      value={formData.recordValue}
                      onChange={(e) =>
                        handleInputChange('recordValue', e.target.value)
                      }
                      className={`${styles.input} ${errors.recordValue ? styles.inputError : ''}`}
                      disabled={isLoading}
                      placeholder={isFieldEvent ? 'e.g., 12.34' : 'e.g., 1:23.45'}
                    />
                    {errors.recordValue && (
                      <span className={styles.errorText}>
                        {errors.recordValue}
                      </span>
                    )}
                  </div>

                  <div className={styles.inputGroup}>
                    <label className={styles.label}>Date Achieved</label>
                    <input
                      type="date"
                      value={formData.achievedAt}
                      onChange={(e) =>
                        handleInputChange('achievedAt', e.target.value)
                      }
                      className={`${styles.input} ${errors.achievedAt ? styles.inputError : ''}`}
                      disabled={isLoading}
                      style={{ color: formData.achievedAt ? '#333' : '#999' }}
                    />
                    {errors.achievedAt && (
                      <span className={styles.errorText}>
                        {errors.achievedAt}
                      </span>
                    )}
                  </div>
                </div>

                {/* Track Surface on its own row for track events and field events with runup */}
                <FormSelect
                  label="Track Surface"
                  name="trackSurface"
                  value={formData.trackSurface}
                  onChange={handleInputChange}
                  options={TRACK_SURFACE_OPTIONS}
                  error={errors.trackSurface}
                  disabled={isLoading}
                  required={false}
                />
              </>
            )}

            {/* Approving Official - always full width */}
            <div className={styles.inputGroup}>
              <label className={styles.label}>Approving Official</label>
              <input
                type="text"
                value={formData.approvedBy}
                onChange={(e) =>
                  handleInputChange('approvedBy', e.target.value)
                }
                className={`${styles.input} ${errors.approvedBy ? styles.inputError : ''}`}
                disabled={isLoading}
                placeholder="Enter official's name"
              />
              {errors.approvedBy && (
                <span className={styles.errorText}>{errors.approvedBy}</span>
              )}
            </div>
          </div>

          <div className={styles.buttons}>
            <CustomButton
              style={{
                ...defaultButtonStyle,
                marginBottom: 9,
                width: 274,
                fontSize: 16,
                fontWeight: 500,
                opacity: isLoading ? 0.6 : 1,
              }}
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading
                ? 'Saving...'
                : mode === 'add'
                  ? 'Add Record'
                  : 'Save Changes'}
            </CustomButton>
          </div>

          {mode === 'edit' && (
            <div className={styles.bottomButton}>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isLoading}
                style={{
                  background: 'none',
                  border: `2px solid ${redColor}`,
                  color: redColor,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  padding: '12px 20px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: isLoading ? 0.6 : 1,
                }}
                title="Delete Record"
              >
                🗑️ {isLoading ? 'Processing...' : 'Delete Record'}
              </button>
            </div>
          )}

          <div className={styles.goBack} onClick={handleClosePopup}>
            <div className={styles.goBackImage}>
              <img src={BackIcon} alt="back" />
            </div>
            <div className={styles.goBackText}>Back</div>
          </div>
        </div>

        <div className={styles.closeBtn} onClick={handleClosePopup}>
          <img src={CloseIcon} alt="close" />
        </div>
      </div>

      <CustomNewConfirm
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title={`Delete ${organisation.title} Record`}
        description={`Are you sure you want to delete this ${organisation.word} record?`}
        confirmButtonText="Delete Record"
        confirmButtonVariant="danger"
        onConfirm={handleDelete}
        cancelButtonText="Cancel"
        showWarning={true}
        warningText="This action cannot be undone. The record will be permanently removed."
        showImpactSummary={true}
        impactItems={[
          { label: 'Event', value: String(selectedRecord?.eventType || '') },
          {
            label: 'Athlete',
            value: String(
              selectedRecord?.athleteName ||
                `${formData.athleteFirstName} ${formData.athleteLastName}`.trim()
            ),
          },
          {
            label: 'Result',
            value: String(
              formatRecordValueForDisplay(
                selectedRecord?.recordValue,
                selectedRecord?.eventType
              ) || formData.recordValue
            ),
          },
          {
            label: words.group,
            value: formatGroupName(
              groupingBasis,
              selectedRecord?.athleteAge || selectedRecord?.ageGroup
            ),
          },
        ]}
        isLoading={isLoading}
      />
    </div>
  );
};

ClubRecordEditPopup.propTypes = {
  showPopup: PropTypes.bool.isRequired,
  closePopup: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['edit', 'add']).isRequired,
  selectedRecord: PropTypes.object,
  eventOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ),
  ageOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ),
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default ClubRecordEditPopup;
